import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Fade,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { RecipeStep } from '../models/Recipe';

interface CookingPromptOverlayProps {
  step: RecipeStep;
  stepIndex: number;
  onPromptClick: (promptText: string) => void;
  onDismiss: () => void;
}

// Analyze step content to determine relevant prompts
const getRelevantPrompts = (stepContent: string): { prompt: string; icon?: string }[] => {
  const content = stepContent.toLowerCase();
  const prompts: { prompt: string; icon?: string }[] = [];

  // Visual check prompts
  if (
    content.includes('brown') ||
    content.includes('golden') ||
    content.includes('color') ||
    content.includes('look') ||
    content.includes('appear')
  ) {
    prompts.push({ prompt: "Does this look right?" });
  }

  // Texture/doneness prompts
  if (
    content.includes('tender') ||
    content.includes('soft') ||
    content.includes('crisp') ||
    content.includes('firm') ||
    content.includes('done')
  ) {
    prompts.push({ prompt: "How do I know when it's ready?" });
  }

  // Seasoning prompts
  if (
    content.includes('salt') ||
    content.includes('season') ||
    content.includes('taste') ||
    content.includes('spice')
  ) {
    prompts.push({ prompt: "How's the seasoning?" });
  }

  // Heat/timing prompts
  if (
    content.includes('simmer') ||
    content.includes('boil') ||
    content.includes('fry') ||
    content.includes('heat') ||
    content.includes('minute')
  ) {
    prompts.push({ prompt: "Is the heat level right?" });
  }

  // Default prompts if none matched
  if (prompts.length === 0) {
    prompts.push({ prompt: "How's it going?" });
    prompts.push({ prompt: "Any questions about this step?" });
  }

  // Add a general issue prompt
  prompts.push({ prompt: "Something seems off..." });

  return prompts.slice(0, 3); // Max 3 prompts
};

const CookingPromptOverlay: React.FC<CookingPromptOverlayProps> = ({
  step,
  stepIndex,
  onPromptClick,
  onDismiss,
}) => {
  const theme = useTheme();

  const prompts = useMemo(
    () => getRelevantPrompts(step.content),
    [step.content]
  );

  return (
    <Fade in timeout={500}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: { xs: 100, md: 120 },
          left: '50%',
          transform: 'translateX(-50%)',
          width: { xs: 'calc(100% - 32px)', sm: 400 },
          maxWidth: 400,
          p: 2,
          borderRadius: 3,
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(44, 36, 22, 0.95)'
            : 'rgba(253, 246, 227, 0.98)',
          border: `2px solid ${theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315'}`,
          zIndex: 1300,
          backdropFilter: 'blur(10px)',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutlineIcon
              sx={{
                color: theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315',
                fontSize: 20,
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315',
              }}
            >
              Quick Check - Step {stepIndex + 1}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onDismiss}
            sx={{
              color: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.6)' : 'rgba(139, 69, 19, 0.6)',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 138, 101, 0.1)'
                  : 'rgba(216, 67, 21, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Prompts */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {prompts.map((item, index) => (
            <Button
              key={index}
              fullWidth
              variant="outlined"
              onClick={() => onPromptClick(item.prompt)}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontFamily: '"Crimson Text", serif',
                fontSize: '1rem',
                color: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.9)' : 'rgba(46, 34, 23, 0.9)',
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 138, 101, 0.3)'
                  : 'rgba(216, 67, 21, 0.3)',
                '&:hover': {
                  borderColor: theme.palette.mode === 'dark' ? '#ff8a65' : '#d84315',
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 138, 101, 0.1)'
                    : 'rgba(216, 67, 21, 0.1)',
                },
                py: 1,
              }}
            >
              {item.prompt}
            </Button>
          ))}
        </Box>

        {/* Dismiss text */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 1.5,
            color: theme.palette.mode === 'dark' ? 'rgba(255, 248, 240, 0.5)' : 'rgba(139, 69, 19, 0.5)',
            fontFamily: '"Crimson Text", serif',
          }}
        >
          Tap outside or X to dismiss
        </Typography>
      </Paper>
    </Fade>
  );
};

export default React.memo(CookingPromptOverlay);
